const operationalIntent = require("./operationalIntent");
const {
  classifyFactualPlanningIntent,
  classifyOperationalIntent,
} = operationalIntent;

export const NEUROGRES_STEP_ORDER = [
  "present",
  "clarify",
  "surface-block",
  "interrupt-pattern",
  "reframe",
  "commit",
  "anchor",
  "follow-through",
] as const;

export type NeurogresActiveStepId = typeof NEUROGRES_STEP_ORDER[number];
export type NeurogresStepId = "environment" | "neuro_link" | NeurogresActiveStepId;

export interface NeurogresStepInference {
  id: NeurogresStepId;
  index: number;
  confidence: "low" | "medium" | "high";
  reason: string;
}

function normalizeText(value = ""): string {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function messageText(entry: unknown): string {
  if (!entry || typeof entry !== "object") return "";
  const message = entry as { content?: string; text?: string };
  return normalizeText(message.content || message.text || "");
}

function latestMessage(messages: unknown[], role?: string): string {
  const wanted = String(role || "").toLowerCase();
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const entry = messages[index] as { role?: string; sender?: string; content?: string; text?: string } | undefined;
    const entryRole = String(entry?.role || entry?.sender || "").toLowerCase();
    if (!wanted || entryRole === wanted || (wanted === "assistant" && entryRole === "nia")) {
      const text = messageText(entry);
      if (text) return text;
    }
  }
  return "";
}

export function inferCurrentStep(messages: unknown[] = []): NeurogresStepInference {
  const latestUser = latestMessage(messages, "user");
  const latestAssistant = latestMessage(messages, "assistant");
  const joined = normalizeText(`${latestUser} ${latestAssistant}`).toLowerCase();
  const operationalIntent = classifyOperationalIntent(messages);
  const factualPlanning = classifyFactualPlanningIntent(messages);

  if (!joined) {
    return { id: "present", index: 1, confidence: "low", reason: "No usable session language yet." };
  }

  if (operationalIntent.operational) {
    return {
      id: "present",
      index: 1,
      confidence: "high",
      reason: "An operational, records, continuity, or escalation request should be handled before coaching-step inference.",
    };
  }

  if (factualPlanning.factualPlanning) {
    return {
      id: "present",
      index: 1,
      confidence: "high",
      reason: "The current message is a factual planning discussion rather than blocking-language evidence.",
    };
  }

  if (/\b(room|space|desk|noise|sleep|hungry|tired|calendar|time|schedule|meeting overload|interruptions|notifications|environment)\b/i.test(joined)) {
    return { id: "environment", index: 0, confidence: "medium", reason: "The main friction appears to be environmental or situational setup." };
  }

  if (/\b(i feel|i'm feeling|i am feeling|in my body|notice what is happening|stay with this|take a breath|slow that down)\b/i.test(joined)) {
    return { id: "neuro_link", index: 0, confidence: "medium", reason: "The session is still linking state awareness before deeper intervention." };
  }

  if (/\b(schedule|follow[\s-]?up|check[\s-]?in|reminder|next week|tomorrow|on monday)\b/i.test(joined)) {
    return { id: "follow-through", index: 8, confidence: "high", reason: "The session is focused on continuation or timing." };
  }

  if (/\b(next step|i will|i'm going to|i am going to|commit|plan|start with|do this today|action)\b/i.test(joined)) {
    return { id: "commit", index: 6, confidence: "high", reason: "The session has moved into action commitment." };
  }

  if (/\b(feels lighter|that helps|i see it now|that makes sense|different now|clearer now)\b/i.test(joined)) {
    return { id: "anchor", index: 7, confidence: "medium", reason: "The conversation shows a shift that can be consolidated." };
  }

  if (/\b(reframe|another way|could mean|instead of|what else is true|not necessarily)\b/i.test(joined)) {
    return { id: "reframe", index: 5, confidence: "medium", reason: "The language points to meaning change or reframing." };
  }

  if (/\b(always|never|can't|cannot|what if|why bother|they make me|no choice)\b/i.test(latestUser)) {
    return { id: "surface-block", index: 3, confidence: "high", reason: "Blocking language is active in the session." };
  }

  if (/\b(stay with me|take a breath|slow that down|notice|what's happening|where do you feel)\b/i.test(joined)) {
    return { id: "interrupt-pattern", index: 4, confidence: "medium", reason: "The assistant is actively interrupting or regulating the pattern." };
  }

  if (/\b(i feel|i'm feeling|i am feeling|what happened|tell me more|say a bit more|what's on your mind)\b/i.test(joined)) {
    return { id: "clarify", index: 2, confidence: "medium", reason: "The conversation is still clarifying the live issue." };
  }

  return { id: "present", index: 1, confidence: "medium", reason: "The safest default is presence before technique." };
}

export function buildStepAlignedFollowUp(step: NeurogresStepInference | null | undefined): string {
  switch (step?.id) {
    case "environment":
      return "Stabilise the environment around the issue and then review what becomes easier to do.";
    case "neuro_link":
      return "Stay with the felt shift and notice what becomes clearer before choosing the next action.";
    case "commit":
      return "Carry the agreed next step into the next 24 hours and notice what helps you follow through.";
    case "anchor":
      return "Revisit what shifted today and repeat the response that made it feel more workable.";
    case "follow-through":
      return "Return at the agreed follow-up point and review what changed since this session.";
    case "reframe":
      return "Notice the old frame when it returns and replace it with the clearer interpretation you reached here.";
    default:
      return "Choose one clear focus to test before the next session, then review what changed.";
  }
}
